// TraccarApiPoller.js
import React, { useEffect, useRef, useState } from 'react';
//import { fetchDevicePositions } from './api'; // your existing file with the API functions

const apiUrl = 'https://admin.monitgps.pl/api'; // Replace with your Traccar API URL
const username = 'api@api.pl'; // Replace with your Traccar username
const password = 'api123'; // Replace with your Traccar password
const authHeader = `Basic ${btoa(`${username}:${password}`)}`; // Authorization header

// Function to fetch device positions and details
export async function fetchDevicePositions() {
  try {
    const [positionsResponse, devicesResponse] = await Promise.all([
      fetch(`${apiUrl}/positions`, {
        headers: {
          'Authorization': authHeader,
        },
      }),
      fetch(`${apiUrl}/devices`, {
        headers: {
          'Authorization': authHeader,
        },
      }),
    ]);

    if (!positionsResponse.ok || !devicesResponse.ok) {
      throw new Error('Failed to fetch device data.');
    }

    const positionsData = await positionsResponse.json();
    const devicesData = await devicesResponse.json();
    // Build a lookup map to avoid O(n^2) .find() calls when merging
    const deviceMap = new Map(devicesData.map(d => [d.id, d]));


    // Combine positions and devices based on device ID.
    // We prioritize device-level attributes (management flags) over 
    // position-level attributes (GPS sensor data) where they overlap.
    const combinedData = positionsData.map((position) => {
      const device = deviceMap.get(position.deviceId);
      
      const posAttrs = position.attributes || {};
      const devAttrs = device?.attributes || {};
      const mergedAttrs = { ...posAttrs, ...devAttrs };

      // Unified SOS Detection: 
      // 1. Explicit CarStatus from our management system
      // 2. Hardware 'sos' flag (boolean or string "true")
      // 3. Hardware 'alarm' attribute (e.g., "sos", "vibration", etc.)
      const isSosActive = 
        mergedAttrs.CarStatus === 'SOS' || 
        mergedAttrs.sos === true || 
        mergedAttrs.sos === 'true' || 
        device?.sos === true ||
        (typeof mergedAttrs.alarm === 'string' && mergedAttrs.alarm.toLowerCase().includes('sos'));

      return {
        ...position,
        deviceName: device?.name || position.deviceName || 'Unknown',
        model: device?.model || position.model || '',
        isSosActive, // Top-level flag for stable UI detection
        attributes: mergedAttrs,
      };
    });

    return combinedData;
  } catch (error) {
    console.error(error);
    return null;
  }
}


function TraccarApiPoller({ onPositionsUpdate, pollMs = 1000 }) {
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);
  const runningRef = useRef(false); // prevent overlapping fetches

  const doFetch = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const combined = await fetchDevicePositions();
      if (!combined) throw new Error('No data returned');

      // Filter out positions where deviceName contains "unknown"
      const filtered = combined.filter(
        (p) => !(p.deviceName || '').toLowerCase().includes('unknown')
      );

      // Merge with previous state: update by deviceId
      onPositionsUpdate((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        filtered.forEach((newPos) => {
          const idx = next.findIndex((pos) => pos.deviceId === newPos.deviceId);
          if (idx > -1) next[idx] = newPos;
          else next.push(newPos);
        });
        return next;
      });

      setIsActive(true);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Polling error:', e);
      setError(e.message || String(e));
      setIsActive(false);
    } finally {
      runningRef.current = false;
    }
  };

  const startPolling = () => {
    if (timerRef.current) return;
    doFetch();
    timerRef.current = setInterval(doFetch, pollMs);
  };

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Start/stop on mount/unmount
  useEffect(() => {
    startPolling();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollMs]);

  // Pause when tab is hidden; resume when visible
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) stopPolling();
      else startPolling();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return (
    <div style={styles.wrapper}>
      <p>
        Status:{' '}
        <span style={{ color: isActive ? 'lime' : 'red' }}>
          {isActive ? 'Polling ✅' : 'Idle ❌'}
        </span>
      </p>
      <p style={{ marginTop: 4 }}>
        Interval: {pollMs} ms
        {lastRefresh && <> | Last: {lastRefresh.toLocaleTimeString()}</>}
      </p>
      {error && <p style={{ color: 'yellow' }}>Error: {error}</p>}
    </div>
  );
}

const styles = {
  wrapper: {
    position: 'fixed',
    bottom: '10px',
    right: '1px',
    width: '230px',
    background: 'rgba(157, 157, 157, 0.8)',
    color: '#fff',
    padding: '10px',
    borderRadius: 5,
    fontSize: 10,
    fontFamily: 'monospace',
    zIndex: 1000,
  },
};

export default TraccarApiPoller;
